import React from 'react'
import PropTypes from 'prop-types'
import { useScramble } from 'use-scramble'

/*
 * Scrambles its text into place on mount (i.e. on page load / navigation).
 *
 * The real text is rendered as the element's children so it's present in the
 * server-rendered HTML — good for SEO, accessibility, no-JS, and no layout
 * shift (the heading reserves its space immediately). use-scramble is SSR-safe
 * (guards on `typeof window`) and, after hydration, takes over the element via
 * a ref and animates the text in.
 *
 * `step: text.length` + `overflow: false` make every character appear on the
 * first frame, so the title fills its final width immediately and the letters
 * scramble *in place* and settle — instead of growing in from an empty string,
 * which shifted the (centered) case-study titles around as they animated. It
 * also makes the duration length-independent: every title settles in the same
 * ~`scramble` frames regardless of how long it is.
 */
const ScrambleText = ({ text, scramble, speed, className }) => {
  const { ref } = useScramble({
    text,
    speed,
    step: text.length,
    overflow: false,
    scramble,
    tick: 1,
    // No underscore ("____") placeholder sweep.
    overdrive: false,
  })

  return (
    <span ref={ref} className={className}>
      {text}
    </span>
  )
}

ScrambleText.propTypes = {
  text: PropTypes.string.isRequired,
  // How many frames each character scrambles before settling. Controls the
  // total duration, independent of title length. Higher = longer/busier.
  scramble: PropTypes.number,
  // 0–1 redraw rate (1 ≈ 60fps).
  speed: PropTypes.number,
  className: PropTypes.string,
}

ScrambleText.defaultProps = {
  scramble: 12,
  speed: 0.6,
  className: undefined,
}

export default ScrambleText
